import {pageAuthorizedByGroup} from "../fragments/PageAuthorization";
import React, {useState} from "react";
import {CurrentStageDisplay, HeaderSlot} from "../../components/onsite/Footer";
import {Breadcrumbs, Button, MenuItem, Paper, Select, Stack, Typography} from "@mui/material";
import {Link} from "react-router-dom";
import {PentaSplit} from "../../components/common/PentaSplit";
import {translateFunction} from "../../localization/TranslationService";
import {ORDERS_CUSTOM} from "./Queries";
import {graphQLServices} from "../../services/GraphQLServices";
import {GraphQlDataGrid} from "../../components/common/grid/GraphQlDataGrid";
import {GlobalState} from "../../global/GlobalState";
import {reportsDefaultTargetStage} from "./ReportModuleOverview";
import {GridColDef} from "@mui/x-data-grid";
import {LazyAccordion} from "../../components/common/containers/LazyAccordion";
import {BREAK, parse, print, visit} from "graphql";
import {ApolloClient, DocumentNode} from "@apollo/client";
import {CodeBlock} from "../../components/common/CodeBlock";
import {graphql} from "cm6-graphql";

const translate = translateFunction("SureBasePortalProductsCatalog");

export const CustomOrdersReport = pageAuthorizedByGroup(function () {
	return <>
		<HeaderSlot><CurrentStageDisplay/></HeaderSlot>
		<PentaSplit
			top={
				<Breadcrumbs style={{margin: "5px"}}>
					<Link style={{color: "inherit", textDecoration: "unset"}} to="/reports">{translate("Reports")}</Link>
					<Typography color="text.primary">{translate("Custom orders report")}</Typography>
				</Breadcrumbs>
			}
			
			center={<Paper style={{
				height: "100%",
			}}>
				<CustomOrdersReportContent/>
			</Paper>}
		/>
	</>;
}, ["Admin"]);

export function CustomOrdersReportContent() {
	const stage = GlobalState.targetKiziStage(reportsDefaultTargetStage);
	
	const clientData = Object.entries(graphQLServices)
		.map(([name, client]) => ({ name, client }));
	
	const [columns, setColumns] = useState<GridColDef[]>([]);
	const [queryDef, setQueryDef] = useState<DocumentNode>(ORDERS_CUSTOM);
	const [customQueryDef, setCustomQueryDef] = useState<DocumentNode>(ORDERS_CUSTOM);
	const [client, setClient] = useState<ApolloClient<any>>(graphQLServices.orderClient);
	const [clients] = useState(clientData);
	const [isOpen, setOpen] = useState(false);
	const [operation, setOperation] = useState("orderDetailsExtended");
	
	
	function flattenKeys(obj: any, parent = "", res : any[] = []): any[] {
		for (const key in obj) {
			if (Object.prototype.hasOwnProperty.call(obj, key)) {
				const propName = parent ? `${parent}.${key}` : key;
				if (typeof obj[key] === "object" && obj[key] !== null) {
					flattenKeys(obj[key], propName, res);
				} else {
					res.push({path: propName, size: obj[key]?.length ?? 0});
				}
			}
		}
		return res;
	}
	
	function getNestedProperty<T>(obj: T, path: string): any {
		if (!path) return undefined;
		// Handle empty string
		const parts = path.split(".");
		return parts.reduce((acc: any, part) => acc && acc[part], obj);
	}
	
	function setTheData(data: any) {
		if (columns.length > 0)
			return;
		
		let propCount = 0;
		let maxIndex = 0;
		let index = 0;
		
		for (const row of data) {
			const fields = flattenKeys(row);
			if (fields.length > propCount) {
				maxIndex = index;
				propCount = fields.length;
			}
			index++;
		}
		
		const fields = flattenKeys(data[maxIndex]);

		let newColumns: GridColDef[] = fields.map(field => {
			return {
				field: field.path,
				headerName: field.path.split(".").at(-1),
				width: field.path.split(".").at(-1).length * 10,
				valueGetter: (params: any) => {
					return getNestedProperty(params.row, field.path);
				}
			};
		});
		newColumns = newColumns.filter((col: GridColDef) => col.field.indexOf("__typename") === -1);
		setColumns(newColumns);
	}
	
	function setCustomQuery(queryText: string) {
		try {
			const queryDocument = parse(queryText);
			setCustomQueryDef(queryDocument);
		} catch (ex) {
			// no op
		}
	}
	
	function applyCustomQuery() {
		setColumns([]);
		
		visit(customQueryDef, {
			Field(node) {
				setOperation(node.name.value);
				return BREAK;
			}
		});
		
		setQueryDef(customQueryDef);
	}
	
	function setApolloClient( a : any) {
		setClient(a.target.value);
	}
	
	return	<>
		<main>
			<LazyAccordion summary={
				<Stack direction="row" flex="1">
					<Typography flex={1}>Query Definition</Typography>
					{isOpen && (
						<Button variant={"contained"} onClick={applyCustomQuery}>Apply</Button>
					)}
				</Stack>
			} onOpened={setOpen}>
				<Select value={client} onChange={setApolloClient}>
					{clients.map((client, index) => (
						<MenuItem key={index} value={client.client}>{client.name}</MenuItem>
					))}
				</Select>
				<Stack>
					<CodeBlock name="QueryDefinition" syntax={graphql()} editable={true} onChange={setCustomQuery}>
						{print(queryDef)}
					</CodeBlock>
				</Stack>
			</LazyAccordion>
			<GraphQlDataGrid
				key={`custom-orders-grid`}
				query={queryDef}
				graphFunction={operation}
				client={client}
				rowsPerPageOptions={[1000]}
				initialPageSize={1000}
				getRowId={row => row.id}
				queryVariables={{stage}}
				columns={columns}
				rows={[]}
				onDataFetched={setTheData}
				enableHeaderFilter={false}
				enableToolbarFilter={false}
			/>
		</main>
	</>;
}
